<template>
  <div class="animated fadeIn">
    <div class="margin-reports">
      <ClientSelection  v-show="isMSMUser"
                        :disabled="isBusy"
                        @clientselectionchanged="onClientSelectionChanged"/>

      <div class="row local-row">
        <div class="col-2">
          <treeselect :options="configurations"
                      :clearable="false"
                      v-model="configurationId"
                      :disabled="!clientSelectionCompleted || !configurations || isBusy"
                      @input="handleConfiguration"
                      @open="configurationOpen=true"
                      :placeholder="$t('Webportal.ConfigurationFilter.Placeholder')" />
        </div>
        <div class="col" style="min-width: 18rem; max-width: 24rem;">
          <treeselect :options="brands"
                      :clearable="true"
                      :limit="2"
                      :disabled="!clientSelectionCompleted || !brands || isBusy"
                      v-model="brandIds"
                      sort-value-by="INDEX"
                      :multiple="true"
                      @input="handleBrand"
                      @open="brandOpen=true"
                      @close="handleBrandClose"
                      :placeholder="$t('Webportal.BrandFilter.Placeholder')" />
        </div>
        <div>
          <b-button v-if="isMarketReportsAdmin"
                    class="btn btn-dark btn-md mr-3"
                    @click="handleEditBrandsAndWaves"
                    v-show="hasPublishStatus && !isPublished"
                    :disabled="!hasPublishStatus || isPublished || isBusy">
            <font-awesome-icon :icon="['fas', 'edit']" /> {{ this.$t('Webportal.Edit')}}
          </b-button>
        </div>

        <span class="pe-3 text-sm" style="min-width: 10rem;">
          {{ getPublishInfo() }}<br>
          {{ getPublishCounts() }}
        </span>
        <div>
          <b-button v-if="isMarketReportsAdmin"
                    class="btn btn-success btn-md mr-3"
                    @click="handlePublish"
                    v-show="hasPublishStatus && !isPublished"
                    :disabled="isBusy">
            <font-awesome-icon :icon="['fas', 'upload']" /> {{ this.$t('Webportal.MarketReports.Publish')}}
          </b-button>
        </div>

        <div class="col" v-show="hasPublishStatus && isPublished && hasNewPublications"></div>

        <div class="col" v-show="hasPublishStatus && isPublished && hasNewPublications">
          - {{ this.$t("Webportal.MarketReports.NewResultsSincePublication")}} -
        </div>
        <div class="col right" style="padding-right: 0rem; min-width: 24rem">
          <b-button v-if="isMSMUser || isMarketReportsAdmin"
                    class="btn btn-danger btn-md"
                    v-show="hasPublishStatus && isPublished"
                    :disabled="isBusy"
                    @click="handleRecallPublication">
            {{ this.$t('Webportal.MarketReports.RecallPublication')}}
          </b-button>

          <!-- Generate -->
          <b-button v-if="isMSMUser || (isMarketReportsAdmin && !isPublished)"
                    class="btn btn-primary btn-md ppt"
                    @click="handleGenerate"
                    :disabled="!hasPublishStatus || isBusy">
            <font-awesome-icon :icon="['fas', 'file-powerpoint']" /> Generate
          </b-button>

          <!-- Upload -->
          <b-button v-if="isMSMUser || (isMarketReportsAdmin && !isPublished)"
                    class="btn btn-primary btn-md ppt"
                    @click="onPickFile"
                    :disabled="!hasPublishStatus || isBusy || configuration.hasDownload || isPublished">
            <font-awesome-icon :icon="['fas', 'file-upload']" /> Upload
          </b-button>
          <input  type="file"
                  style="display: none"
                  ref="fileInput"
                  accept="application/vnd.openxmlformats-officedocument.presentationml.presentation"
                  @change="handleUpload" />

          <!-- Delete -->
          <b-button v-if="isMSMUser || (isMarketReportsAdmin && !isPublished)"
                    class="btn btn-primary btn-md ppt"
                    @click="handleDeleteUploadClick"
                    :disabled="!hasPublishStatus || isBusy || configuration.hasDownload === false || isPublished">
            <font-awesome-icon :icon="['fas', 'ban']" /> Delete
          </b-button>

          <b-button v-if="isMarketReportsUser || isMarketReportsAdmin"
                    class="btn btn-primary btn-md ppt"
                    @click="handleDownload"
                    :disabled="!hasPublishStatus || isBusy || configuration.hasDownload === false">
            <font-awesome-icon :icon="['fas', 'file-download']" /> Download
          </b-button>
        </div>
      </div>

      <div class="legend-container text-sm" style="min-height:1.625rem;">
        <div v-if="dashboardData && !isBusy">
          <div  class="legend-item"
                v-for="(item, index) in legendItems"
                :key="index">
              <div style="float: left; text-align: left; padding-right: 0.25rem;" :style="{color: item.color }">
                <font-awesome-icon :icon="['fas', 'square']" height="12" width="12" />
              </div>
              <div style="float: left; text-align: left; padding-right: 0.25rem;">
                <span style="font-size: 10px;">{{ item.label }} </span>
              </div>
          </div>
        </div>
      </div>

      <div  class="market-reports-grid"
            v-if="configuration"
            v-bind:class="{ spinner: isBusy }">
        <div class="A1 market-reports-item">
          <WaveDetailsPanel :configuration="configuration"
                            @edit="handleEditWaveDetails"
                            :header="$t('Webportal.MarketReports.WaveDetails')"
                            :isEditable="isMarketReportsAdmin && !isPublished"
                            :tableData="waveDetailTables"
                            :waveDetails="waveDetails" />
        </div>
        <div class="A2 market-reports-item">
          <QualityIndexPanel  :brandMode="brandMode"
                              :configuration="configuration"
                              :data="qualityIndexData"
                              @edit="handleEditWaveDetails"
                              :header="$t('Webportal.MarketReports.DevelopmentOfTheQualityIndex')"
                              :isEditable="false"
                              :waves="qualityIndexWaves"
                              :waveSettings="waveColors" />
        </div>
        <div class="A3 market-reports-item">
          <KeyProcessPanel  :brandMode="brandMode"
                            :configuration="configuration"
                            @edit="handleEditKeyProcessSteps"
                            :header="$t('Webportal.MarketReports.KeyProcessSteps')"
                            :isEditable="isMarketReportsAdmin && !isPublished"
                            :kpis="keyProcessStepsKpis"
                            :kpiBase="dashboardData?.Kpi || []"
                            :waves="keyProcessStepsWaves" />
        </div>
        <div class="A4 market-reports-item">
          <ProcessQualityPanel  :brandMode="brandMode"
                                :configuration="configuration"
                                :data="processQualityData"
                                @edit="handleEditProcessQuality"
                                :header="$t('Webportal.MarketReports.QualityIndexAndProcessQuality')"
                                :isEditable="isMarketReportsAdmin && !isPublished"
                                :waves="processQualityPanelWaves" />
        </div>
        <div class="A5 market-reports-item">
          <SubjectiveQuestionsPanel :brandIds="brandIds"
                                    :brandMode="brandMode"
                                    :brands="options?.brands || []"
                                    :configuration="configuration"
                                    @edit="handleEditSubjectiveQuestions"
                                    :header="$t('Webportal.MarketReports.SubjectiveQuestions')"
                                    :isEditable="isMarketReportsAdmin && !isPublished"
                                    :kpi="subjectiveQuestionsKpi"
                                    :average="subjectiveQuestionsAverage"
                                    :questionData="subjectiveQuestionsData"
                                    :questions="questionTexts"
                                    :waves="lastWaves" />
        </div>
      </div>
    </div>

    <b-modal ref="editBrandsAndWavesModal" scrollable centered size="xl">
      <template v-slot:modal-title>
        <strong>{{ this.$t("Webportal.MarketReports.BrandAndWaveAppearance") }}</strong>
      </template>

      <div class="modal-body">
        <!-- Brands and customized colors -->
        <h5><strong>{{ this.$t("Webportal.MarketReports.Brands")}}:</strong></h5>
        <div class="modal-row">

          <div class="row underline-2">
            <div class="col-3">
              <strong>{{ this.$t("Webportal.MarketReports.Labels") }}</strong>
            </div>
            <div class="col-2">
              <strong>{{ this.$t("Webportal.MarketReports.Colors") }}</strong>
            </div>
          </div>

          <div class="row modal-line" v-for="(brand, index) in brandColors" :key="index">
            <div class="col-3">
              {{ brand.label }}
            </div>
            <div class="col-2">
              <dx-color-box v-model="brand.color"
                            @valueChanged="onInput($event)"

                            stylingMode="underlined" />
            </div>
          </div>
        </div>

        <!-- Waves and customized labels, visibility, text-visibility and colors -->
        <div class="row">
          <div class="col-3">
            <h5><strong>{{ this.$t("Webportal.MarketReports.Customize") }} {{ this.$t("Webportal.MarketReports.Waves") }}:</strong></h5>
          </div>
          <div class="col-2"></div>
          <div class="col-7">
            <h5><strong>{{ this.$t("Webportal.MarketReports.OnlyDevelopmentOfTheQualityIndexPanel") }}</strong></h5>
          </div>
        </div>

        <div class="row underline-2">
          <div class="col-3">
            <strong>{{ this.$t("Webportal.MarketReports.Labels") }}</strong>
          </div>
          <div class="col-2">
            <strong>{{ this.$t("Webportal.MarketReports.Colors") }}</strong>
          </div>
          <div class="col-4">
            <strong>{{ this.$t("Webportal.MarketReports.BrandsPptxOnly") }}</strong>
          </div>
          <div class="col">
            <!-- <div class="col-2 right text-sm"> -->
            <b-form-checkbox  :indeterminate="isDrawDataIndeterminate"
                              :checked="allDrawDataChecked"
                              :key="refreshData"
                              @change="toggleAllDrawData">
              <strong><div v-html="$t('Webportal.MarketReports.DrawDataShort')"></div></strong>
            </b-form-checkbox>
          </div>
          <div class="col">
            <b-form-checkbox  :indeterminate="isDrawTextIndeterminate"
                              :checked="allDrawTextChecked"
                              :key="refreshText"
                              @change="toggleAllDrawText">
              <strong><div v-html="$t('Webportal.MarketReports.DrawTextShort')"></div></strong>
            </b-form-checkbox>
          </div>

        </div>
        <div class="row modal-line" v-for="(wave, index) in waveColors" :key="index">
          <div class="col-3">
            <b-form-input type="text"
                          :class="[ { 'text-is-set': wave.alternativeLabel }]"
                          :placeholder="wave.name"
                          v-model="wave.alternativeLabel">
            </b-form-input>
          </div>
          <div class="col-2">
            <dx-color-box v-model="wave.color"
                          @valueChanged="onInput($event)"
                          stylingMode="underlined" />
          </div>
          <div class="col-4">
            <treeselect :options="brands"
                        :clearable="true"
                        :limit="2"
                        openDirection="auto"
                        v-model="wave.brandIds"
                        sort-value-by="INDEX"
                        :multiple="true"
                        :placeholder="$t('Webportal.BrandFilter.Placeholder')" />
          </div>
          <div class="col">
            <b-form-checkbox  v-model="wave.drawData"
                              name="checkbox-2"
                              @change="checkDrawDataItemClicked(wave)">
            </b-form-checkbox>
          </div>
          <div class="col">
            <b-form-checkbox  v-model="wave.drawText"
                              :indeterminate="!allDrawDataChecked && !isDrawDataIndeterminate"
                              name="checkbox-3"
                              :disabled="!wave.drawData"
                              @change="checkDrawTextItem">
            </b-form-checkbox>
          </div>

        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="float-end">
          <b-button type="button"
                    @click="cancelEditBrandsAndWaves"
                    class="btn btn-secondary"
                    :disabled="isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t("Webportal.Cancel") }}
          </b-button>
          <b-button type="button"
                    @click="saveEditBrandsAndWaves"
                    class="btn btn-primary"
                    :disabled="!canSaveAppearance || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal ref="editWaveDetailsModal" scrollable centered size="lg">
      <template v-slot:modal-title>
        <strong>{{ this.$t('Webportal.MarketReports.WaveDetails') }}</strong>
      </template>

      <div class="container modal-body" v-if="activeConfiguration">
        <div class="row flexed">
          <div class="col-6">
            <strong>{{ this.$t('Webportal.MarketReports.WaveDetails') }}</strong>
          </div>
          <div class="col-6 right">
            <b-button type="button"
                      @click="newWaveDetail"
                      class="btn btn-secondary btn-md"
                      :disabled="activeConfiguration.isPublished">
              <font-awesome-icon :icon="['fas', 'plus']" /> {{ $t("Webportal.New") }}
            </b-button>
          </div>
        </div>
        <div  class="row"
              v-for="(detail, index) in activeConfiguration.waveDetails"
              :key="index">
          <div class="col-md-3">
            <b-form-group :label="$t('Webportal.MarketReports.Header_')">
              <b-form-input id="Header"
                            :disabled="activeConfiguration.isPublished"
                            v-model="detail.header"
                            size="md"
                            :state="getStateDetailHeader(detail)">
              </b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-2">
            <b-form-group :label="$t('Webportal.MarketReports.Order_')">
              <b-form-input id="SortOrder"
                            :disabled="activeConfiguration.isPublished"
                            v-model="detail.sortOrder"
                            size="md"
                            :state="getStateDetailSortOrder(detail)">
              </b-form-input>
            </b-form-group>
          </div>
          <div class="col">
            <b-form-group :label="$t('Webportal.MarketReports.DetailText_')">
              <b-form-textarea  id="Text"
                                :disabled="activeConfiguration.isPublished"
                                v-model="detail.text"
                                size="md"
                                rows="3">
              </b-form-textarea>
            </b-form-group>
          </div>
          <div class="col-md-auto">
            <b-button type="button"
                      @click="deleteWaveDetail(index)"
                      style="margin-top: 1.75rem;"
                      :disabled="activeConfiguration.isPublished"
                      class="btn btn-danger btn-md">
              <font-awesome-icon icon="trash-alt" /> {{ $t('Webportal.Delete') }}
            </b-button>
          </div>
        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="float-end">
          <b-button type="button"
                    @click="cancelEditWaveDetails"
                    class="btn btn-secondary"
                    :disabled="isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t("Webportal.Cancel") }}
          </b-button>
          <b-button type="button"
                    @click="saveWaveDetails"
                    class="btn btn-primary"
                    :disabled="!canSaveWaveDetails || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal ref="editKeyProcessStepsModal" scrollable centered size="lg">
      <template v-slot:modal-title>
        <strong>{{ this.$t("Webportal.MarketReports.KeyProcessSteps") }}</strong>
      </template>

      <div class="modal-body">
        <strong>{{ this.$t('Webportal.MarketReports.Customize') }} {{ this.$t('Webportal.MarketReports.ClusterTexts') }}</strong>
        <div class="row modal-row">
          <div  class="col-12 modal-line"
                v-for="(item, index) of getEditableKeyProcessStepsClusters"
                :key="index">
            <b-form-group label-for="text-input"
                          :description="getKeyProcessStepsClusterDescription(item)"
                          class="mb-0">
              <b-form-input type="text"
                            id="text-input"
                            :class="[ { 'text-is-set': item.alternativeClusterLabel && item.alternativeClusterLabel !== item.cluster }]"
                            :placeholder="item.cluster"
                            v-model="item.alternativeClusterLabel">
              </b-form-input>
            </b-form-group>
          </div>
        </div>
        <strong>{{ this.$t('Webportal.MarketReports.Customize') }} {{ this.$t('Webportal.MarketReports.KpiTexts') }}</strong>
        <div class="row modal-row">
          <div  class="col-12 modal-line"
                v-for="(item, index) of getEditableKeyProcessStepsKpis"
                :key="index">
            <b-form-group label-for="text-input"
                          :description="getKeyProcessStepsKpiDescription(item)"
                          class="mb-0">
              <b-form-textarea  id="text-input"
                                :class="[ { 'text-is-set': item.alternativeKpiLabel && item.alternativeKpiLabel !== item.kpi }]"
                                v-model="item.alternativeKpiLabel"
                                :placeholder="item.kpi"
                                rows="3"
                                no-resize
                                max-rows="3">
              </b-form-textarea>
            </b-form-group>
          </div>
        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="float-end">
          <b-button type="button"
                    @click="cancelEditKeyProcessSteps"
                    class="btn btn-secondary"
                    :disabled="isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t("Webportal.Cancel") }}
          </b-button>
          <b-button type="button"
                    @click="saveEditKeyProcessSteps"
                    class="btn btn-primary"
                    :disabled="!canSaveKeyProcessSteps || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal ref="editProcessQualityModal" scrollable centered size="lg">
      <template v-slot:modal-title>
        <strong>{{ this.$t("Webportal.MarketReports.ProcessQuality") }}</strong>
      </template>

      <div class="modal-body">
        <strong class="modal-line" style="font-size: 12pt;">{{ this.$t('Webportal.MarketReports.Customize') }} {{ this.$t('Webportal.MarketReports.KpiTexts') }}</strong>
        <div class="row modal-row">
          <div  class="col-12 modal-line"
                v-for="(item, index) of activeProcessQualityData"
                :key="index">
            <b-form-group label-for="text-input"
                          :description="getEditProcessQualityDescription(item)"
                          class="mb-0">
              <b-form-input type="text"
                            id="text-input"
                            :key="refresh"
                            :class="[ { 'text-is-set': item.xAxisAlternative && item.xAxisAlternative !== item.xAxis }]"
                            :placeholder="item.xAxis"
                            v-model="item.xAxisAlternative">
              </b-form-input>
            </b-form-group>
          </div>
        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="float-end">
          <b-button type="button"
                    @click="cancelEditProcessQuality"
                    class="btn btn-secondary"
                    :disabled="isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t("Webportal.Cancel") }}
          </b-button>
          <b-button type="button"
                    @click="saveEditProcessQuality"
                    class="btn btn-primary"
                    :disabled="!canSaveProcessQuality || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal ref="editSubjectiveQuestionsModal" scrollable centered size="lg">
      <template v-slot:modal-title>
        <strong>{{ this.$t("Webportal.MarketReports.SubjectiveQuestions") }}</strong>
      </template>

      <div class="modal-body">
        <div  class="row underline-2">
          <div class="col-4" style="font-weight:600;">
            KPI
          </div>
          <div  class="col-2 center text-sm"
                v-for="(wave, index) in subjectiveQuestionWaves"
                :key="index">
            {{ wave.label }}
          </div>
          <div class="col-2 right text-sm">
            <b-form-checkbox  :indeterminate="isSubjectiveQuestionIndeterminate"
                              :checked="allSubjectiveQuestionChecked"
                              :key="refresh"
                              @change="toggleAllSubjectiveQuestions">
            </b-form-checkbox>
          </div>
        </div>

        <div  class="modal-row">
          <div  class="row modal-line underline-1"
                v-for="(item, index) in subjectiveQuestionItems"
                :key="index">
            <div class="col-4">
              {{ item.label }}
            </div>
            <div  class="col-2 center"
                  v-for="(wave, j) in subjectiveQuestionWaves"
                  :key="j">
              <font-awesome-icon  size="lg"
                                  :icon="['fas', item.value[j] === true ? 'check' : 'times']"
                                  :style="{ 'color': item.value[j] === true ? 'green' : 'red' }" />
            </div>
            <div class="col-2 right">
              <b-form-checkbox  v-model="item.enabled"
                                name="checkbox-1"
                                @change="checkSubjectiveQuestionItem">
              </b-form-checkbox>
            </div>
          </div>
        </div>
      </div>

      <template v-slot:modal-footer>
        <div class="float-end">
          <b-button type="button"
                    @click="cancelEditSubjectiveQuestions"
                    class="btn btn-secondary"
                    :disabled="isBusy">
            <font-awesome-icon :icon="['far', 'ban']" /> {{ $t("Webportal.Cancel") }}
          </b-button>
          <b-button type="button"
                    @click="saveEditSubjectiveQuestions"
                    class="btn btn-primary"
                    :disabled="!canSaveKeyProcessSteps || isBusy">
            <font-awesome-icon :icon="['far', 'dot-circle']" /> {{ $t("Webportal.Save") }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <b-modal ref="modalDialog" size="sm" hide-footer :title="dialog.title">
      <div class="d-block text-center">
        <span>{{ dialog.content }}</span>
      </div>
      <div class="w-100 text-center">
        <b-button class="m-2" variant="secondary" @click="hideModalDialog" v-if="dialog.delete" >Cancel</b-button>
        <b-button class="m-2 btn-md w-25" :variant="dialog.delete ? 'danger' : 'secondary'" @click="hideModalDialog">Ok</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script lang="js">
import AppConfig from "@/mixins/AppConfig-APP_TARGET";
import AuthenticationService from '@/services/AuthenticationService';
import MarketReportsService from '@/services/MarketReportsService';
import DownloadFileMixin from '@/mixins/DownloadFileMixin';

import ClientSelection from '@/components/ClientSelection'
import KeyProcessPanel from '@/components/MarketReports/KeyProcessPanel.vue';
import ProcessQualityPanel from '@/components/MarketReports/ProcessQualityPanel.vue';
import QualityIndexPanel from '@/components/MarketReports/QualityIndexPanel.vue';
import SubjectiveQuestionsPanel from '@/components/MarketReports/SubjectiveQuestionsPanel.vue';
import WaveDetailsPanel from '@/components/MarketReports/WaveDetailsPanel.vue';
import { DxColorBox } from 'devextreme-vue';

import MarketReportsWaveDetailsMixin from '@/mixins/MarketReportsWaveDetailsMixin';
import MarketReportsProcessQualityMixin from '@/mixins/MarketReportsProcessQualityMixin';
import MarketReportsAudiMixin from '@/mixins/MarketReportsAudiMixin';
import moment from "moment";

export default {
  mixins: [
    AppConfig, DownloadFileMixin,
    MarketReportsWaveDetailsMixin, MarketReportsProcessQualityMixin,
    MarketReportsAudiMixin
  ],

  components: {
    ClientSelection,
    KeyProcessPanel, ProcessQualityPanel, QualityIndexPanel,
    SubjectiveQuestionsPanel, WaveDetailsPanel,
    DxColorBox
  },

  data() {
    return {
      brandOpen: false,
      brandIds: [],
      brandIdsCurrent: [],
      brandMode: false,
      clientId: null,
      clientSelectionCompleted: false,
      clientSettings: null,
      configurationOpen: false,
      configurationId: null,
      dashboardData: null,

      isBusy: false,
      isExporting: false,

      options: null,

      waveDetails: [],
      waveDetailTables: null,

      qualityIndexData: [],
      qualityIndexWaves: [],

      keyProcessStepsKpis: [],
      keyProcessStepsKpisEdit: [],
      keyProcessStepsWaves: [],
      processQualityPanelWaves: [],

      processQualityData: [],
      processQualityWaves: [],

      questionTexts: [],
      subjectiveQuestionItems: [],
      subjectiveQuestionWaves: [],
      subjectiveQuestionsData: [],
      subjectiveQuestionsKpi: [],
      subjectiveQuestionsAverage: [],

      allSubjectiveQuestionChecked: false,
      isSubjectiveQuestionIndeterminate: false,

      allDrawDataChecked: false,
      isDrawDataIndeterminate: false,

      allDrawTextChecked: false,
      isDrawTextIndeterminate: false,

      waves: [
        { id:'audi', label:'Audi', color: 'red'},
        { id:'bmw', label:'BMW', color: 'cornflowerblue'},
        { id:'mercedes', label:'Mercedes', color: 'silver'}
      ],

      brandColors: [],
      waveColors: [],

      defaultWaveColors: [ '#F50437', '#000' , '#C6C6C6' ],

      refresh: 0,
      refreshData: 0,
      refreshText: 0,

      dialog: {
        title: 'Hello',
        content: 'Hello, World!',
        delete: false
      }
    };
  },

  computed: {
    isMSMUser: function () {
      return AuthenticationService.isMSMUser;
    },
    isMarketReportsUser() {
      return AuthenticationService.localUserProfile.Permissions.includes('Market Reports');
    },
    isMarketReportsAdmin() {
      return AuthenticationService.localUserProfile.Permissions.includes('Market Reports Admin');
    },
    configurations() {
      return this.options ? this.options.configurations : null;
    },
    configuration() {
      return this.options && this.options.configurations
        ? this.options.configurations.find(x => x.id === this.configurationId)
        : null;
    },
    brands() {
      return this.options ? this.options.brands : null;
    },

    // can Save predicates for Modal Dialogs
    canSaveAppearance() {
      return true;
    },
    canSaveWaveDetails() {
      return true;
    },
    canSaveKeyProcessSteps() {
      return true;
    },
    canSaveProcessQuality() {
      return true;
    },
    canSaveSubjectiveQuestions() {
      return true;
    },

    lastWaves() {
      if (this.dashboardData && this.dashboardData.Waves) {
        return this.getLastWaves(this.dashboardData.Waves, false, true);
      } else {
        return this.waves;
      }
    },

    hasPublishStatus() {
      return this.dashboardData && this.dashboardData.PublishStatus && this.dashboardData.PublishStatus.length === 1;
    },

    isPublished() {
      if (!this.hasPublishStatus) return false;
      return this.dashboardData.PublishStatus[0].IsPublished === 1;
    },

    hasNewPublications() {
      if (!this.isPublished) return false;
      return this.dashboardData.PublishStatus[0].ChecksAfterPublish > 0;
    },

    getEditableKeyProcessStepsClusters() {
      return _.uniqBy(this.keyProcessStepsKpisEdit, 'SurveySectionGridId');
    },
    getEditableKeyProcessStepsKpis() {
      return _.uniqBy(this.keyProcessStepsKpisEdit, 'KpiId');
    },

    legendItems() {
      if (this.brandMode) {
        return this.getSortedBrands(this.brands, this.brandIds);
      } else {
        return this.waves;
      }
    }
  },

  methods: {
    getPublishInfo() {
      if (!this.hasPublishStatus) return '';
      let result = this.$t('Webportal.MarketReports.PublishDate') + ': ';
      if (this.isPublished) {
        let date = moment(this.dashboardData.PublishStatus[0].PublishedAt, moment.ISO_8601);
        result += date.format('DD.MM.YYYY'); // ('DD.MM.YYYY@HH:mm');
      } else {
        result += this.$t('Webportal.MarketReports.NotPublished');
      }
      return result;
    },
    getPublishCounts() {
      let pub = this.dashboardData?.PublishStatus[0];
      if (!pub) return '';
      return `${this.$t('Webportal.MarketReports.FinishedTotalChecks')}: ${pub.CompletedChecks}/${pub.AllChecks}`;
    },

    handleConfiguration(event) {
      if (this.clientSelectionCompleted && !this.configurationOpen)
        return;

      this.brandIds = [ this.options.brands.find(x => x.isMain).id ];
      this.configurationOpen = false;
      this.loadFilters(this.clientSelectionCompleted);
    },

    handleBrand(event) {
      if (this.brandOpen) {
        this.brandLastEvent = event;
        return;
      }
      this.loadDashboardData();
    },

    handleBrandClose(event) {
      this.brandOpen = false;
      if (this.brandLastEvent) {
        this.handleBrand(this.brandLastEvent);
        this.brandLastEvent = null;
      }
    },

    loadDashboardData() {
      this.isBusy = true;
      this.dashboardData = null;

      MarketReportsService.getDashboardData(this.configurationId, this.brandIds)
        .then((res) => {
          this.dashboardData = res.data;
          this.brandIdsCurrent = [...new Set(this.dashboardData.CurrentVisits.map(x => x.Brand))];
          // Control 1
          this.waveDetails = this.dashboardData.WaveDetailText || [];
          this.waveDetailTables = this.getWaveDetailTables(this.dashboardData);

          // if up to 1 brand, get last 3 waves, else get all brands as competitors
          let brandMode = this.brandIds ? this.brandIds.length > 1 : false;
          this.waves = this.getLastWaves(this.dashboardData.Waves, brandMode, true);

          this.qualityIndexData = this.getQualityIndexData(this.dashboardData, brandMode);
          this.qualityIndexWaves = this.getQualityIndexWaves(this.dashboardData.Waves, brandMode);

          this.processQualityData = this.getProcessQualityData(
            [ ...this.dashboardData?.Kpi.filter(x => x.KpiGroupName === 'Mandatory Clusters' || x.KpiGroupName === 'Optional Clusters') || [] ],
            brandMode);
          this.processQualityWaves = [];

          let kpsKpi = this.getKeyProcessStepsKpis(
            this.dashboardData?.Kpi.filter(x => x.KpiGroupName === 'Key Process Steps') || [],
            brandMode);
          kpsKpi.sort((a, b) => a.id - b.id);
          this.keyProcessStepsKpis =  kpsKpi;

          let kpsWaves = this.getLastWaves(this.dashboardData.Waves, brandMode, false, this.brandIdsCurrent);
          this.keyProcessStepsWaves = this.getKeyProcessStepsWaves(this.keyProcessStepsKpis, kpsWaves, brandMode);
          this.processQualityPanelWaves = kpsWaves;
          this.brandMode = brandMode;

          // Get unique 'KpiCode' from this.dashboardData.SubjectiveQuestions and map to id and text
          let questionIds = _.uniq(_.map(this.dashboardData.SubjectiveQuestions, 'KpiCode'));
          this.questionTexts = questionIds.map((x) => ({ id: x, label: x }));

          this.subjectiveQuestionsData = this.dashboardData?.SubjectiveQuestions || [];
          this.subjectiveQuestionsKpi = this.dashboardData?.Kpi || [];
          this.subjectiveQuestionsAverage = this.dashboardData?.SubjectiveQuestionsAverage || [];
          this.waveColors = JSON.parse(JSON.stringify(this.dashboardData.Waves));
        })
        .catch(error => {
          console.error("An error occurred:", error);
        })
        .finally(() => {
          this.isBusy = false;
        });
    },

    getWaveDetailTables(data) {
      let result = { schedule: { planned: {}, current: {} }, visits: [], enquiry: [] };
      result.schedule.planned.start = data.WaveDetailWaves[0].WavePlannedSince;
      result.schedule.planned.end = data.WaveDetailWaves[0].WavePlannedUntil;
      result.schedule.current.start = data.WaveDetailWaves[0].WaveSince;
      result.schedule.current.end = data.WaveDetailWaves[0].WaveUntil;

      if (data.CurrentWaveDistribution) {
        for (const enquiry of data.CurrentWaveDistribution) {
          let item = result.enquiry.find(x => x.id === `${enquiry.Brand}${enquiry.Model}`);
          if (!item) {
            item = {
              id: `${enquiry.Brand}${enquiry.Model}`,
              Brand: enquiry.Brand,
              Model: enquiry.Model };
            result.enquiry.push(item);
          }
          item.Current = enquiry.Distribution;
        }
      }
      if (data.PlannedWaveDistribution) {
        for (const enquiry of data.PlannedWaveDistribution) {
          let item = result.enquiry.find(x => x.id === `${enquiry.Brand}${enquiry.Model}`);
          if (!item) {
            item = {
              id: `${enquiry.Brand}${enquiry.Model}`,
              Brand: enquiry.Brand,
              Model: enquiry.Model };
            result.enquiry.push(item);
          }
          item.Planned = enquiry.Distribution;
        }
      }

      if (data.PlannedVisits) {
        for (const visit of data.PlannedVisits) {
          let item = result.visits.find(x => x.Brand === visit.Brand);
          if (!item) {
            item = { Brand: visit.Brand, frequencies: [] };
            result.visits.push(item);
          }
          let freq = item.frequencies.find(x => x.Frequency === visit.Frequency);
          if (!freq) {
            freq = { Frequency: visit.Frequency, Planned: visit.Visits };
            item.frequencies.push(freq);
          } else {
            console.error(`Duplicate frequency ${visit.Frequency} for brand ${visit.Brand}`);
          }
        }
      }
      if (data.CurrentVisits) {
        for (const visit of data.CurrentVisits || []) {
          let item = result.visits.find(x => x.Brand === visit.Brand);
          if (!item) {
            item = { Brand: visit.Brand, frequencies: [] };
            result.visits.push(item);
          }
          let freq = item.frequencies.find(x => x.Frequency === visit.Frequency);
          if (!freq) {
            freq = { Frequency: visit.Frequency, Current: visit.Visits };
            item.frequencies.push(freq);
          } else {
            freq.Current = visit.Visits;
          }
        }
      }

      for (const enquiry of result.enquiry) {
        let opt = this.options.brands.find(x => x.id.toLowerCase() === enquiry.Brand.toLowerCase());
        if (opt) {
          enquiry.color = opt.color;
        }
      }
      for (const visit of result.visits) {
        let opt = this.options.brands.find(x => x.id.toLowerCase() === visit.Brand.toLowerCase());
        if (opt) {
          visit.color = opt.color;
        }
      }

      result.Planned = 0;
      for (const visit of result.visits) {
        for (const freq of visit.frequencies) {
          if (!freq.Planned) // can be undefined
            continue;
          result.Planned += freq.Planned * freq.Frequency;
        }
      }
      result.Current = this.dashboardData?.PublishStatus[0]?.CompletedChecks;
      return result;
    },

    getProcessQualityData(kpis, brandMode) {
      let realKpi = kpis.filter(x => this.brandIds.includes(x.Brand));
      realKpi = _.orderBy(realKpi, ['KpiGroupCode', 'KpiId'], ['asc', 'asc']);
      let items =
        _.chain(realKpi)
        .uniqBy('KpiCode')
        .map((item) => ({ xAxis: item.KpiCode, group: item.KpiGroupCode }))
        .value() || [];

      // insert item at the start
      let total = { xAxis: this.$t('Webportal.MarketReports.Total') };
      if (!brandMode) {
        for (const wave of this.waves) {
          let item = this.dashboardData.OverallByBrandAndWave
            .filter(x => x.ExternalSurveyWaveId == wave.id).slice(-1);
          if (item && item.length === 1) {
            total[wave.id] = item[0].result * 100;
          } else {
            total[wave.id] = undefined;
          }
        }
      } else {
        for (const wave of this.qualityIndexWaves) {
          let item  = this.dashboardData.OverallByBrandAndWave
            .filter(x => x.brand === wave.id).slice(-1);
          if (item && item.length === 1) {
            total[wave.id] = item[0].result * 100;
          } else {
            total[wave.id] = undefined;
          }
        }
      }

      items.unshift(total);
      for (const kpi of kpis) {
        let item = items.find(x => x.xAxis === kpi.KpiCode);
        if (!item)
          item[brandMode ? kpi.Brand : kpi.ExternalSurveyWaveId] = null;
        else {
          if (brandMode && kpi.Wave !== this.lastWaves[0].name) {
            continue;
          }
          item[brandMode ? kpi.Brand : kpi.ExternalSurveyWaveId] = kpi.Result * 100;
          if (!item.KpiId) {
            item.KpiId = kpi.KpiId;
            if (kpi.alternativeKpiLabel) {
              item.xAxisAlternative = kpi.alternativeKpiLabel;
            }
          } else {
            if (item.KpiId !== kpi.KpiId) {
              console.error(`KpiId mismatch ... Item: ${item.KpiId} vs. Kpi: ${kpi.KpiId}`, item, kpi);
            }
          }
        }
      }

      let withoutEmpties = [];
      for (const item of items) {
        if (item.KpiId || item.xAxis === 'Total')
          withoutEmpties.push(item);
      }

      items = withoutEmpties;
      for (const item of items) {
        item.x = this.splitHeaderText(item.xAxisAlternative || item.xAxis);
      }

      // number the items, so we can draw a vertical line between the items (x.5). When using Text keys, the chart will only draw
      // directly overthe items.
      for (let i = 0; i < items.length; i++) {
        items[i].xNumber = i;
        let item = items[i];
        if (!brandMode) {
          for (const wave of this.waves) {
            let result = item[parseInt(wave.id)];
            if (result === undefined) {
              item[wave.id] = -1; // < 0 will result in 'N/A'
            }
          }
        }
      }

      // only return items with at least 1 data point
      return items;
    },

    splitHeaderText(text) {
      if (text.length <= 10) {
        return text; // Return the text as is if it's short enough
      }

      const words = text.split(' ');
      let line = '';
      let result = '';

      words.forEach(word => {
        if ((line + word).length > 12) { // Check if current line is too long
          result += line.trim() + '\n'; // Add the current line to result
          line = ''; // Reset current line
        }

        if (line.length + word.length >= 10 || line === '') {
          line += word + ' '; // Add word to current line
        } else {
          // If adding the next word would make the line too short, move to next line
          result += line.trim() + '\n';
          line = word + ' ';
        }
      });

      return result + line.trim(); // Add the last line
    },

    getQualityIndexWaves(data, brandMode) {
      if (!brandMode) {
        let brand = this.options.brands.find(x => x.isMain);
        return [{ id: brand.id, color: brand.color, label: brand.label }];
      }
      return this.getLastWaves(this.waves, brandMode, false);
    },

    getQualityIndexData(data, brandMode)
    {
      if (!brandMode) {
        return data.OverallByBrandAndWave.map(
          (x) => ({ x: x.alternativeLabel ? x.alternativeLabel : x.wave, xBase: x.wave, [this.brandIds[0]]: x.result * 100, [this.brandIds[0] + 'Tooltip']: x.result }));
      }

      let results = [];
      for (const item of data.OverallByBrandAndWave) {
        let existing = results.find(x => x.x === item.wave);
        if (existing) {
          existing[item.brand] = item.result * 100;
          existing[item.brand + 'Tooltip'] = item.result;
          continue;
        }
        results.push({
          x: item.alternativeLabel ? item.alternativeLabel : item.wave,
          xBase: item.wave,
          [item.brand]: item.result * 100,
          [item.brand + 'Tooltip']: item.result });
      }
      return results;
    },

    getKeyProcessStepsWaves(kpis, waves, brandMode){
      var results = [];

      // find all waves that have at least 1 result
      for (const wave of waves) {
        if (kpis.some(x => x.results.some(y => y.key === wave.id))) {
          results.push(wave);
        }
      }

      // At least add the first wave
      if (results.length == 0)
        results.push(waves[0]);

      return results;
    },

    getKeyProcessStepsKpis(kpis, brandMode) {
      let items = [];
      let uniqueValues = [...new Set(kpis.map(item => item.KpiPositionInGroup))];
      let lastWave = this.lastWaves[0];
      let lastWaveId = parseInt(lastWave.id);

      for (const id of uniqueValues) {
        let local = kpis.filter(x => x.KpiPositionInGroup === id);
        let item = {
          results: [],
          id: id,
          SurveySectionGridId: local[0].SurveySectionGridId,
          KpiId: local[0].KpiId,
          kpi: local[0].KpiNameLong,
          alternativeKpiLabel: local[0].alternativeKpiLabel,
          cluster: local[0].defaultGridText,
          alternativeClusterLabel: local[0].alternativeGridText
        }

        if (brandMode && local[0].ExternalSurveyWaveId !== lastWaveId) {
          continue;
        }

        for (const kpiItem of local) {
          let actualResult = item.results.find(x => x.key === (brandMode ? kpiItem.Brand : kpiItem.ExternalSurveyWaveId.toString()));
          if (!actualResult) {
            actualResult = { key: brandMode ? kpiItem.Brand : kpiItem.ExternalSurveyWaveId.toString() };
            if (lastWaveId === kpiItem.ExternalSurveyWaveId) {
              actualResult.value = this.multiplyPreserveDecimalPlaces(kpiItem.Result);
            } else if (this.lastWaves[1].id === kpiItem.ExternalSurveyWaveId.toString()) {
              actualResult.lastValue = this.multiplyPreserveDecimalPlaces(kpiItem.Result);
            }
            item.results.push(actualResult);
          } else {
            let lastId = this.lastWaves[1].id;
            if (kpiItem.ExternalSurveyWaveId.toString() === lastId) {
              actualResult.lastValue = this.multiplyPreserveDecimalPlaces(kpiItem.Result);
            }
          }
        }
        items.push(item);
      }

      return items;
    },

    setWaveDefaultColors(items, defaults) {
      for (let i = 0; i < items.length; i++) {
        const element = items[i];
        if (element.color === null) {
          element.color = defaults[i];
        }
      }
      return items;
    },

    getLastWaves(waves, brandMode, slicing, ids) {
      if (!ids) {
        ids = this.brandIds;
      }
      if (!brandMode) {
        let items = this.dashboardData.Waves
          .sort((a, b) => b.sortOrder - a.sortOrder)
          .slice(0, 3)
          .map((x) => ({ id: x.ExternalSurveyWaveId.toString(), sortOrder: x.sortOrder, name: x.name, label: x.label, color: x.color || null }));

        return this.setWaveDefaultColors(items, this.defaultWaveColors);
      }
      let brands = slicing ? this.options.brands.slice(0, 3) : this.options.brands;
      // return all Brands where id is in the brandIds
      let results = brands.filter(x => ids.includes(x.id))
        .map((x) => ({ id: x.id, sortOrder: x.sortOrder, label: x.label, name: x.name, color: x.color, logo: x.logo }));
      return this.orderAudiFirst(results);
    },

    loadFilters(reload) {
      this.isBusy = true;
      MarketReportsService.getFilters(this.clientId, this.configurationId)
        .then((filter) => {
          let opt = filter.data;

          let defaultItem = opt.brands.find(x => x.id.toLowerCase().includes('audi'));
          if (!defaultItem) throw new Error('No Main Brand found', opt.brands);
          defaultItem.isDisabled = true;
          defaultItem.isMain = true;

          this.options = opt;
          if (this.configurationId === null) {
            this.configurationId = this.options.configurations[0].id;
            this.brandIds.push(this.options.brands.find(x => x.isMain).id);
          }

          if(reload) {
            this.loadDashboardData();
          }
        })
        .catch(error => {
          console.error("An error occurred:", error);
        })
        .finally(() => {
        });
    },

    onClientSelectionChanged(clientSettings) {
      if (clientSettings) {
        this.clientSettings = clientSettings;
        this.clientId = clientSettings.ClientId ? clientSettings.ClientId : null;
      }
      this.clientSelectionCompleted = true;
      this.loadFilters();
    },

    handlePublish(event) {
      this.isBusy = true;
      MarketReportsService.postPublishConfiguration(this.configurationId)
        .then((res) => {
          this.loadFilters(true);
        })
        .catch((error) => {
          console.error(error)
        });
    },

    handleRecallPublication(event) {
      this.isBusy = true;
      MarketReportsService.postUnpublishConfiguration(this.configurationId)
        .then((res) => {
          this.loadFilters(true);
        })
        .catch((error) => {
          console.error(error)
        });
    },

    handleGenerate(event) {
      this.isExporting = true;
      this.isBusy = true;
      MarketReportsService.getPowerpointReportFile(this.clientId, this.configurationId)
        .then(response => {
          this.downloadFile(response);
          this.isExporting = false;
          this.isBusy = false;
        })
        .catch(_ => {
          this.isExporting = false;
          this.isBusy = false;
        } );
    },

    handleDownload(event) {
      this.isExporting = true;
      this.isBusy = true;
      MarketReportsService.getPowerpointReportDownload(this.clientId, this.configurationId)
        .then(response => {
          this.downloadFile(response);
          this.isExporting = false;
          this.isBusy = false;
        })
        .catch(_ => {
          this.isExporting = false;
          this.isBusy = false;
        } );
    },

    handleUpload(event) {
      if (!event.target.files[0].name.endsWith('.pptx')) {
        this.dialog.title = 'File type not supported';
        this.dialog.content = 'Please upload a .pptx file.';
        this.dialog.delete = false;
        this.$refs.modalDialog.show();
        return;
      }

      this.isExporting = true;
      this.isBusy = true;
      const formData = new FormData();
      formData.append('file', event.target.files[0]);

      MarketReportsService.postPowerpointReportFile(this.clientId, this.configurationId, formData)
        .then(response => {
          this.configuration.hasDownload = true;
          this.isExporting = false;
          this.isBusy = false;
        })
        .catch(_ => {
          this.configuration.hasDownload = false;
          this.isExporting = false;
          this.isBusy = false;
        });
    },

    deleteUpload(event) {
      this.isExporting = true;
      this.isBusy = true;
      MarketReportsService.deletePowerpointReportFile(this.clientId, this.configurationId)
        .then(response => {
          this.configuration.hasDownload = false;
          this.isExporting = false;
          this.isBusy = false;
        })
        .catch(_ => {
          this.isExporting = false;
          this.isBusy = false;
        } );
    },

    // Edit Brands and Waves
    handleEditBrandsAndWaves(event) {
      this.brandColors = JSON.parse(JSON.stringify(this.options.brands));
      let waveCols = JSON.parse(JSON.stringify(this.dashboardData.Waves));
      for (const wave of waveCols) {
        if (!wave.excludedBrands) {
          wave.brandIds = [ ...this.options.brands.map(x => x.id) ];
        } else {
          wave.brandIds = [ ...this.options.brands.map(x => x.id).filter(x => !wave.excludedBrands.split(',').includes(x)) ];
        }
      }
      this.waveColors = waveCols;

      this.checkDrawDataItem();
      this.checkDrawTextItem();
      this.$refs.editBrandsAndWavesModal.show();
    },

    cancelEditBrandsAndWaves() {
      this.$refs.editBrandsAndWavesModal.hide();
    },
    saveEditBrandsAndWaves(event) {
      this.isBusy = true;
      let waves = JSON.parse(JSON.stringify(this.waveColors));
      for (const wave of waves) {
        wave.brandIds = [ ...this.options.brands.map(x => x.id).filter(x => !wave.brandIds.includes(x)) ];
      }

      MarketReportsService.postBrandAndWaveColors(this.configurationId,
        {
          brands: this.brandColors,
          waves: waves
        })
        .then((brandRes) => {
          this.loadFilters(true);
        })
        .catch(error => {
          console.error("An error occurred:", error);
        })
        .finally(() => {
          this.isBusy = false;
          this.$refs.editBrandsAndWavesModal.hide();
        });
    },

    // Edit Wave Details
    handleEditWaveDetails(event) {
      MarketReportsService.getMarketReportsConfiguration(this.clientId, this.configurationId)
        .then((res) => {
          this.activeConfiguration = this.getConfigurationsFixed(res.data)[0];
          this.$refs.editWaveDetailsModal.show();
        })
        .catch(error => {
          console.error("An error occurred:", error);
          this.activeConfiguration = null;
        });
    },
    cancelEditWaveDetails() {
      this.$refs.editWaveDetailsModal.hide();
    },


    // Edit Key Process Steps
    handleEditKeyProcessSteps(event) {
      let items = JSON.parse(JSON.stringify(this.keyProcessStepsKpis));
      for (const item of items) {
        if (!item.alternativeKpiLabel) {
          item.alternativeKpiLabel = item.kpi;
        }
        if (!item.alternativeClusterLabel) {
          item.alternativeClusterLabel = item.cluster;
        }
      }
      this.keyProcessStepsKpisEdit = items;
      this.$refs.editKeyProcessStepsModal.show();
    },
    cancelEditKeyProcessSteps() {
      this.keyProcessStepsKpisEdit = [];
      this.$refs.editKeyProcessStepsModal.hide();
    },
    saveEditKeyProcessSteps(event) {
      this.isBusy = true;
      MarketReportsService.postKpiTexts(this.configurationId,
        {
          kpis: this.getEditableKeyProcessStepsKpis
            .map(x => ({
              id: x.KpiId,
              text: (x.alternativeKpiLabel && x.alternativeKpiLabel !== x.kpi) ? x.alternativeKpiLabel : null })),
          sections: this.getEditableKeyProcessStepsClusters
            .map(x => ({
              id: x.SurveySectionGridId,
              text: (x.alternativeClusterLabel && x.alternativeClusterLabel !== x.cluster) ? x.alternativeClusterLabel : null }))
        })
        .then((brandRes) => {
          this.loadFilters(true);
        })
        .catch(error => {
          console.error("An error occurred:", error);
        })
        .finally(() => {
          this.isBusy = false;
          this.$refs.editKeyProcessStepsModal.hide();
          this.keyProcessStepsKpisEdit = [];
        });
    },
    getKeyProcessStepsClusterDescription(item) {
      if (item.alternativeClusterLabel && item.alternativeClusterLabel !== item.cluster) {
        return `${this.$t('Webportal.MarketReports.Default')}: ${item.cluster}`;
      }
    },
    getKeyProcessStepsKpiDescription(item) {
      if (item.alternativeKpiLabel && item.alternativeKpiLabel !== item.kpi) {
        return `${this.$t('Webportal.MarketReports.Default')}: ${item.kpi}`;
      }
    },

    // Edit Quality Index and Process Quality
    handleEditProcessQuality(event) {
      let items = JSON.parse(JSON.stringify(this.processQualityData))
        .filter(x => x.xAxis !== 'Total');
      for (const item of items) {
        if (!item.xAxisAlternative) {
          item.xAxisAlternative = item.xAxis;
        }
      }
      this.activeProcessQualityData = items;
      this.$refs.editProcessQualityModal.show();
    },
    cancelEditProcessQuality() {
      this.$refs.editProcessQualityModal.hide();
    },
    saveEditProcessQuality(event) {
      this.isBusy = true;
      MarketReportsService.postKpiTexts(this.configurationId,
        {
          kpis: this.activeProcessQualityData.filter(x => x.KpiId)
            .map(x => ({
              id: x.KpiId,
              text:  (x.xAxisAlternative && x.xAxisAlternative !== x.xAxis) ? x.xAxisAlternative : null })),
          sections: []
        })
        .then((brandRes) => {
          this.loadFilters(true);
        })
        .catch(error => {
          console.error("An error occurred:", error);
        })
        .finally(() => {
          this.isBusy = false;
          this.$refs.editProcessQualityModal.hide();
        });
    },
    getEditProcessQualityDescription(item) {
      if (item.xAxisAlternative && item.xAxisAlternative !== item.xAxis) {
        return `${this.$t('Webportal.MarketReports.Default')}: ${item.xAxis}`;
      }
    },

    // Edit Subjective Questions
    getSubjectiveQuestionItems() {
      let results = [];
      for (const item of this.questionTexts) {
        let questionResults = this.subjectiveQuestionsData.filter(x => x.KpiCode === item.id);
        let kpi = this.dashboardData.Kpi.find(x => x.KpiCode === item.id);
        let result = {
          id: item.id,
          label: item.label,
          kpiId: kpi?.KpiId,
          enabled: kpi && kpi.HideKpi === 0 ? true : false,
          value: [ false, false, false ]
        };
        for (const [index, wave] of this.subjectiveQuestionWaves.entries()) {
          // "123" == 123 => true
          let hit = questionResults.find(x => x.ExternalSurveyWaveId == wave.id);
          if (hit) {
            result.value[index] = true;
          }
        }

        if (kpi && result.value.some(x => x === true)) {
          results.push(result);
        }
      }
      return results;
    },

    handleEditSubjectiveQuestions(event) {
      // TODO: clone data if necessary
      this.subjectiveQuestionWaves = this.getLastWaves(this.dashboardData.Waves, false, false).reverse();
      this.subjectiveQuestionItems = this.getSubjectiveQuestionItems();
      this.checkSubjectiveQuestionItem();
      this.$refs.editSubjectiveQuestionsModal.show();
    },
    cancelEditSubjectiveQuestions() {
      this.$refs.editSubjectiveQuestionsModal.hide();
      this.subjectiveQuestionItems = [];
      this.subjectiveQuestionWaves = [];
    },

    saveEditSubjectiveQuestions(event) {
      this.isBusy = false;
      let data = this.subjectiveQuestionItems.map(x => (
        {
          ConfigurationId: this.configurationId,
          KpiId: x.kpiId,
          IsVisible: x.enabled
        }));

      MarketReportsService.postKpiVisibilityForPowerpoint(data)
        .then((brandRes) => {
          this.loadFilters(true);
        })
        .catch(error => {
          console.error("An error occurred:", error);
        })
        .finally(() => {
          this.isBusy = false;
          this.$refs.editSubjectiveQuestionsModal.hide();
          this.subjectiveQuestionItems = [];
          this.subjectiveQuestionWaves = [];
        });
    },

    checkSubjectiveQuestionItem(item, index) {
      this.refresh++;
      const allChecked = this.subjectiveQuestionItems.every(x => x.enabled);
      const anyChecked = this.subjectiveQuestionItems.some(x => x.enabled);

      this.allSubjectiveQuestionChecked = allChecked;
      this.isSubjectiveQuestionIndeterminate = anyChecked && !allChecked;
    },
    toggleAllSubjectiveQuestions(newValue) {
      this.subjectiveQuestionItems.forEach(x => x.enabled = newValue);
      this.isSubjectiveQuestionIndeterminate = false;
    },

    checkDrawDataItemClicked(item) {
      this.checkDrawDataItem();
      if (item.drawData === false) {
        item.drawText = false;
      }
    },
    checkDrawDataItem() {
      this.refreshData++;
      const allChecked = this.waveColors.every(x => x.drawData);
      const anyChecked = this.waveColors.some(x => x.drawData);

      this.allDrawDataChecked = allChecked;
      this.isDrawDataIndeterminate = anyChecked && !allChecked;
    },
    toggleAllDrawData(newValue) {
      this.waveColors.forEach(x => x.drawData = newValue);
      this.allDrawDataChecked = newValue;
      this.isDrawDataIndeterminate = false;
    },

    checkDrawTextItem() {
      this.refreshText++;
      const allChecked = this.waveColors.every(x => x.drawText);
      const anyChecked = this.waveColors.some(x => x.drawText);

      this.allDrawTextChecked = allChecked;
      this.isDrawTextIndeterminate = anyChecked && !allChecked;
    },
    toggleAllDrawText(newValue) {
      this.waveColors.forEach(x => x.drawText = newValue);
      this.allDrawTextChecked = newValue;
      this.isDrawTextIndeterminate = false;
    },

    onPickFile () {
      this.$refs.fileInput.click();
    },

    hideModalDialog(event) {
      if (this.dialog.delete) {
        this.deleteUpload();
      }
      this.$refs.modalDialog.hide()
    },

    handleDeleteUploadClick() {
      this.dialog.title = 'Delete file';
      this.dialog.content = "This action removes the actually provisioned .pptx file? This can't be made undone!";
      this.dialog.delete = true;
      this.$refs.modalDialog.show();
    }
  }
};

</script>

<style scoped lang="scss">

.spinner::after {
  top: 40vh!important;
}
.market-reports-grid {
  display: grid;
  grid-template-columns: 22% 34% 44%;
  grid-template-rows: 27vh 27vh 27fr;
  gap: 0.5rem 0.5rem;
  padding: 0.5rem 1rem 1rem 0.5rem;
  min-width: 100%;
  min-height: 100%;
  grid-auto-flow: row;
  grid-template-areas:
    "A1 A2 A3"
    "A4 A4 A3"
    "A5 A5 A5";
}
.A1 {
  grid-area: A1;
}
.A2 {
  grid-area: A2;
}
.A3 {
  grid-area: A3;
}
.A4 {
  grid-area: A4;
}
.A5 {
  grid-area: A5;
  min-height: 27vh;
}

.client-selection {
  padding: 0rem 0rem 0.5rem 0rem;
  margin-left: -26px;
  margin-top: 0px;
}
.local-row {
  margin: 0rem 0rem;
}
.col, .col-1, .col-2 {
  padding-right: 0.5rem;
  padding-left: 0.5rem;
}

.modal-line {
  padding-top: 0.25rem;
  padding-bottom: 0.75rem;
}

.modal-row {
  padding-bottom: 3rem;
}

.text-is-set {
  border: 2px solid black;
  color: black;
  font-weight: 600;
}

.center {
  text-align: center;
}

.text-sm {
  font-size: 0.75rem;
}

.underline-1 {
  border-bottom: 1px solid black;
}

.underline-2 {
  border-bottom: 2px solid black;
}

.legend-container {
  display: flex;
  padding-top: 0.5rem;
  margin-left: 0.75rem;
  // min-width: 10rem;
  // background: yellow;
}



.legend-item {
  float: left;
  text-align: left;
  min-width: 4rem;
  margin-right:1rem;
}
</style>
